import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Subject, switchMap } from 'rxjs';
import { TaskDataService } from 'src/app/modules/sale/task/task.data.service';
import { ToasterService } from 'src/app/services/core/toaster.service';
import { HelperService } from 'src/app/services/helper.service';
import { debounceTime, distinctUntilChanged, first } from 'rxjs/operators';
import { BackendApiService } from 'src/app/services/backend-api.service';
import { MatMenuTrigger } from '@angular/material/menu';
import { DialogService } from 'src/app/services/core/dialog.service';
import { NgDialogAnimationService } from 'src/app/services/core/ng-dialog-animation.service';
import { ViewTaskDetailComponent } from 'src/app/modules/sale/task/view-task-detail/view-task-detail.component';
import { SharedDataService } from '../../shared.data.service';
import { UserService } from 'src/app/services/core/user.service';
import { resolveProfileImageUrl } from 'src/app/utils/utils.function';

@Component({
    selector: 'app-task-dropdown',
    templateUrl: './task-dropdown.component.html',
    styleUrls: ['./task-dropdown.component.scss'],
    standalone: false
})
export class TaskDropdownComponent implements OnInit {
  tasksList: any[] = [];
  tasksMeta: any[] = [];
  taskCount: any;
  public priority: any = [
    { id: 3, name: 'High', color: 'red' },
    { id: 2, name: 'Medium', color: 'green' },
    { id: 1, name: 'Low', color: 'orange' },
  ];
  params = {
    per_page: 10,
    page: 1,
    loadingService: 0,
    sort_by: 'id',
    sort_order: 'DESC',
  };
  isRequestInProgress = false;
  search: string = '';
  private searchInput = new Subject<string>();
  private readonly debounceTimeMs = 500;
  showLoadMoreOption: boolean = false;
  isLoading: boolean = false;
  // True when doing a full reload (initial open, search, filter change)
  isReloading: boolean = false;
  currentTask: any = null;
  pendingTaskCount: number = 0;

  // View mode for density toggle
  viewMode: 'comfortable' | 'compact' = 'comfortable';
  private readonly VIEW_MODE_STORAGE_KEY = 'task-dropdown-view-mode';
  // Task filter state with localStorage persistence
  public taskFilter: 'non-completed' | 'completed' | 'all' = 'all';
  private readonly TASK_FILTER_STORAGE_KEY = 'task-dropdown-filter';

  @ViewChild('taskMenu') taskMenu!: MatMenuTrigger;
  @ViewChild('statusMenu') statusMenu!: MatMenuTrigger;
  @ViewChild('takeoffMenu') takeoffMenu!: MatMenuTrigger;
  @ViewChild('childMenuTrigger') childMenuTrigger!: MatMenuTrigger;
  @ViewChild('filterMenuTrigger') filterMenuTrigger!: MatMenuTrigger;
  @ViewChild('taskMenuTrigger') taskMenuTrigger!: MatMenuTrigger;
  editTaskPermission = 'TASK-EDIT';
  deleteTaskPermission = 'TASK-DELETE';
  changeStatusTaskPermission: any = 'TASK-CHANGE-STATUS';
  viewDetailTaskPermission = 'TASK-VIEW-DETAIL';
  changePriorityTaskPermission = 'TASK-CHANGE-PRIORITY';
  exportPDFPermission = 'TASK-EXPORT-PDF';
  changeStageTaskPermission = 'TASK-CHANGE-STAGE';

  constructor(
    private taskDataService: TaskDataService,
    public toasterService: ToasterService,
    private helperService: HelperService,
    public backendApiService: BackendApiService,
    public dialog: NgDialogAnimationService,
    public dialogService: DialogService,
    private sharedDataService: SharedDataService,
    private currentUser: UserService
  ) {}

  ngOnInit(): void {
    this.searching();
    this.loadViewMode();
    this.loadTaskFilter();
    this.loadPendingCount();
  }

  loadViewMode(): void {
    const savedMode = localStorage.getItem(this.VIEW_MODE_STORAGE_KEY);
    if (savedMode === 'compact' || savedMode === 'comfortable') {
      this.viewMode = savedMode;
    }
  }

  /**
   * Load saved task filter from localStorage
   * Defaults to 'all' if no saved filter exists or invalid value
   */
  loadTaskFilter(): void {
    const savedFilter = localStorage.getItem(this.TASK_FILTER_STORAGE_KEY);
    if (
      savedFilter === 'non-completed' ||
      savedFilter === 'completed' ||
      savedFilter === 'all'
    ) {
      this.taskFilter = savedFilter;
    } else {
      this.taskFilter = 'all'; // Default for new users
    }
  }

  setViewMode(mode: 'comfortable' | 'compact'): void {
    this.viewMode = mode;
    localStorage.setItem(this.VIEW_MODE_STORAGE_KEY, mode);
  }

  setTaskFilter(filter: 'non-completed' | 'completed' | 'all'): void {
    this.taskFilter = filter;
    // Save filter preference to localStorage
    localStorage.setItem(this.TASK_FILTER_STORAGE_KEY, filter);
    this.params.page = 1;
    this.getTasksList();
  }

  getFilterLabel(): string {
    switch (this.taskFilter) {
      case 'completed':
        return 'Completed';
      case 'all':
        return 'All';
      case 'non-completed':
      default:
        return 'Active';
    }
  }

  onMenuOpen() {
    if (!this.isRequestInProgress) {
      // Load saved filter preference from localStorage
      this.loadTaskFilter();
      this.params.page = 1;
      this.getTasksList();
    }
    // refresh pending count when user opens the menu
    this.loadPendingCount();
  }

  getTasksList() {
    if (this.isRequestInProgress) {
      return;
    }
    this.isRequestInProgress = true;
    this.isLoading = true;
    // When requesting the first page, treat it as a full reload: clear list immediately
    this.isReloading = this.params.page === 1;
    if (this.isReloading) {
      this.tasksList = [];
      this.showLoadMoreOption = false;
    }

    const param = {
      ...this.params,
      search: this.search,
      user_id: this.currentUser.getID(),
      task_filter: this.getBackendTaskFilter(),
      is_task_count: 1,
    };

    this.taskDataService
      .list(param)
      .pipe(first())
      .subscribe({
        next: (res: any) => {
          const newTasksList = res.data;
          if (this.params.page === 1) {
            this.tasksList = newTasksList;
          } else {
            this.tasksList = [...this.tasksList, ...newTasksList];
          }
          this.tasksMeta = res.meta;
          this.taskCount = res.task_counts;
          this.showLoadMoreOption = this.helperService.shouldLoadMore(
            this.tasksList,
            this.tasksMeta
          );
        },
        error: (err: any) => {
          this.isRequestInProgress = false;
          this.isLoading = false;
          this.isReloading = false;
          this.toasterService.showErrorMessage('Failed to load tasks');
        },
        complete: () => {
          this.isRequestInProgress = false;
          this.isLoading = false;
          this.isReloading = false;
        },
      });
  }

  private loadPendingCount() {
    const userId: any = this.currentUser.getID();
    if (!userId) {
      return;
    }
    this.taskDataService
      .getPendingCount(userId)
      .pipe(first())
      .subscribe({
        next: (res: any) => {
          // API returns { success: true, count: number }
          this.pendingTaskCount = Number(res?.count) || 0;
        },
      });
  }

  onChangeStatus(item: any, priority: any) {
    event?.stopPropagation();
    this.backendApiService
      .postApi(`tasks/update-priority/${item}`, { priority: priority?.id })
      .subscribe((response: any) => {
        this.toasterService.showSuccessMessage(response?.data?.message);
        this.getTasksList();
        if (this.taskMenu) {
          this.taskMenu.closeMenu();
        }
      });
  }

  setCurrentTask(task: any) {
    this.currentTask = task;
  }

  // Close only the child filter menu and keep the parent task menu open
  onFilterMenuItemClick(event: any) {
    event?.stopPropagation();
    if (event?.preventDefault) {
      event.preventDefault();
    }
    if (this.filterMenuTrigger) {
      this.filterMenuTrigger.closeMenu();
    }
    // Do not touch parent menu; letting it stay open naturally
  }

  onTakeoffMenuClick(event: any, task: any) {
    event?.stopPropagation();
    this.childMenuTrigger.closeMenu();
    this.sharedDataService
      .updateRequestTakeoffStatus(
        task?.request_takeoff?.id,
        task?.request_takeoff?.status === 1 ? 2 : 3
      )
      .subscribe({
        next: (response: any) => {
          this.toasterService.showSuccessMessage(response?.message);
          this.getTasksList();
          if (this.taskMenu) {
            this.taskMenu.closeMenu();
          }
        },
      });
  }

  openTaskDetail(task: any) {
    this.dialogService.onOpenFromRightDialog(
      ViewTaskDetailComponent,
      {
        id: task?.id,
        permissions: {
          viewDetailTaskPermission: this.viewDetailTaskPermission,
          editTaskPermission: this.editTaskPermission,
          deleteTaskPermission: this.deleteTaskPermission,
          changeStatusTaskPermission: this.changeStatusTaskPermission,
          changePriorityTaskPermission: this.changePriorityTaskPermission,
          exportPDFPermission: this.exportPDFPermission,
          changeStageTaskPermission: this.changeStageTaskPermission,
        },
      },
      1300
    );
    this.dialogService.afterClose()?.subscribe((result: any) => {
      this.getTasksList();
    });
  }

  getPriorityDetail(id: string) {
    const priority = this.priority.find((x: any) => x.id == id);
    return priority;
    // return priority ? priority?.name : '--';
  }

  onSearchTextChange() {
    this.searchInput.next(this.search);
  }

  onSearch() {
    if (!this.search) {
      this.params.page = 1;
      this.searchInput.next(this.search);
    }
  }

  searching() {
    this.searchInput
      .pipe(
        debounceTime(this.debounceTimeMs),
        distinctUntilChanged(),
        switchMap(async () => {
          this.params.page = 1;
          this.getTasksList();
        })
      )
      .subscribe();
  }

  loadMoreData($event: any) {
    $event.stopPropagation();
    this.showLoadMoreOption = this.helperService.shouldLoadMore(
      this.tasksList,
      this.tasksMeta
    );
    if (this.showLoadMoreOption) {
      this.params.page += 1;
      this.getTasksList();
    }
  }

  ngOnDestroy() {
    this.searchInput.unsubscribe();
  }

  mapUsers(users: any[]) {
    if (!users) return [];
    return users.map((user) => ({
      name: user.full_name,
      url: resolveProfileImageUrl(user?.profile_image),
      staffID: user?.attachable?.id,
      userStatus: user?.attachable?.user_status,
    }));
  }

  // Due date badge calculation
  getDueDateBadge(task: any): string | null {
    if (!task.due_date) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dueDate = new Date(task.due_date);
    dueDate.setHours(0, 0, 0, 0);

    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'OVERDUE';
    if (diffDays === 0) return 'TODAY';
    if (diffDays === 1) return 'TOMORROW';
    if (diffDays <= 7) return `${diffDays}d`;

    return null; // Don't show badge for far-future tasks
  }

  isDueOverdue(task: any): boolean {
    if (!task.due_date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(task.due_date);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < today;
  }

  isDueUrgent(task: any): boolean {
    if (!task.due_date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(task.due_date);
    dueDate.setHours(0, 0, 0, 0);
    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 3;
  }

  // Map UI filter to backend expected values
  private getBackendTaskFilter():
    | 'all_task'
    | 'active_task'
    | 'completed_task' {
    switch (this.taskFilter) {
      case 'completed':
        return 'completed_task';
      case 'non-completed':
        return 'active_task';
      case 'all':
      default:
        return 'all_task';
    }
  }

  // Progress bar color
  getProgressColor(progress: number): string {
    if (progress === 100) return '#28a745'; // Green - complete
    if (progress >= 67) return '#20c997'; // Teal - good progress
    if (progress >= 34) return '#ff6600'; // Orange - moderate
    return '#dc3545'; // Red - low progress
  }

  hasChecklist(task: any): boolean {
    return task.checklist_total && task.checklist_total > 0;
  }

  // Description preview
  getDescriptionPreview(description: string): string {
    if (!description) return '';
    const maxLength = 60;
    if (description.length <= maxLength) return description;
    return description.substring(0, maxLength).trim() + '...';
  }

  // Ensure task name starts with a capital letter (display purpose only)
  formatTaskName(name: any): string {
    if (typeof name !== 'string') {
      return name ?? '';
    }
    const trimmed = name.trim();
    if (!trimmed) {
      return '';
    }
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  }

  // Get first letter of status for compact badge
  getStatusInitial(statusName: string | undefined): string {
    if (!statusName) return 'N';
    return statusName.charAt(0).toUpperCase();
  }

  // Get priority initial (H/M/L)
  getPriorityInitial(priorityId: number): string {
    const priorityMap: { [key: number]: string } = {
      3: 'H', // High
      2: 'M', // Medium
      1: 'L', // Low
    };
    return priorityMap[priorityId] || '-';
  }

  // Mark task as complete
  markAsComplete(task: any, event: any): void {
    event?.stopPropagation();

    // Guard here too, not only in the template, so a stray call cannot re-complete
    // an already-completed task.
    if (this.isTaskCompleted(task)) {
      return;
    }

    // Store original status for error rollback
    const originalStatus = task.status;
    const originalIsCompleted = task.is_completed;
    const originalStatusLabel = task.status_label
      ? { ...task.status_label }
      : null;

    // Immediately update local task object for instant UI feedback. The real
    // status id comes back from the server (it is per-office configuration, not
    // a fixed 3), so only the completion flag and label are set optimistically.
    task.is_completed = true;
    if (task.status_label) {
      task.status_label.name = 'Completed';
    } else {
      task.status_label = {
        name: 'Completed',
        color: '#28a745', // Green color for completed
      };
    }

    this.backendApiService
      .patchApi(`tasks/mark-completed/${task.id}`, {})
      .subscribe(
        (response: any) => {
          this.toasterService.showSuccessMessage(
            response?.data?.message || 'Task marked as completed'
          );
          this.getTasksList(); // Refresh to get server-side updates
          // Close the parent tasks dropdown after completing
          if (this.taskMenu) {
            this.taskMenu.closeMenu();
          }
        },
        (error: any) => {
          // Revert local changes on error
          task.status = originalStatus;
          task.is_completed = originalIsCompleted;
          task.status_label = originalStatusLabel;
          this.toasterService.showError(
            error?.error?.message || 'Failed to mark task as completed'
          );
        }
      );
  }

  /**
   * Completion comes from the API (`is_completed`), which derives it from the
   * status's awarded/accepted or lost flag — the same rule the list filter, the
   * counts and mark-completed use.
   *
   * This used to test `status === 3` or the name 'completed'. Task statuses are
   * per-office configuration (here they are ids 31-35, named Inactive/Active/
   * New/Bad/Sure), so that test never matched and the Complete action stayed
   * enabled on tasks that were already complete.
   */
  isTaskCompleted(task: any): boolean {
    return task?.is_completed === true;
  }
}
